import { LoadingOverlay, showToast } from "@/components/feedback";
import { createAddress } from "@/lib";
import { forwardGeocode } from "@/lib/geocoding";
import {
  geocodePrecision,
  PIN_DRIFT_LIMIT_METERS,
  pinOutOfReach,
  type AnchorPrecision,
  type PinAnchor,
} from "@/lib/address-pin";
import { Address } from "@/types";
import { addressSchema, AddressSchemaType } from "@/types/forms";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePageBackground } from "@/hooks/use-page-background";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { basicInfoAtom, deliveryServiceAtom } from "@/atoms";
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config";
import { useAtomValue } from "jotai";
import { AddressDetailsForm } from "./new-address/AddressDetailsForm";
import { AddressEntryStep } from "./new-address/AddressEntryStep";
import { AddressFinishStep } from "./new-address/AddressFinishStep";
import { AddressMapStep } from "./new-address/AddressMapStep";
import { useAddressEntryGeocoding } from "./new-address/use-address-entry-geocoding";
import type { LocationPickerOption } from "./LocationPickerField";
import { Heading, IconButton, PageContainer, Text } from "@/components/ui";
import { Back } from "@/icons";
import { getAddressServiceAreaStatus } from "@/pages/cart/checkout/components/service-area";

/**
 * Address flow (iFood / Uber / 99Food style):
 *
 *  1. entry   — CEP (free APIs) or GPS (one reverse geocode); see
 *               `useAddressEntryGeocoding` for both paths.
 *  2. details — street/number/district/complement (NO live geocode while typing)
 *  3. map     — on continue: FORWARD geocode of the filled address places the
 *               pin and draws the green drift circle (`PIN_DRIFT_LIMIT_METERS`).
 *               The pin drags inside that circle. Outside it, confirm stays
 *               disabled. Dragging never rewrites street, number or district.
 *  4. finish  — confirming the pin keeps the street, number, district, city, UF
 *               and CEP the customer typed. Only complement and reference edit.
 *
 * NEIGHBORHOOD mode skips entry and map. City-less: city defaults to the
 * store's but stays editable, state is locked to the store's UF, district
 * comes from the flat neighborhood list. With cities: BOTH city and district
 * are closed pickers (`LocationPickerField`) — no free typing of the city —
 * picking a city sets state to that city's UF and restricts the district list
 * to its neighborhoods. Mode and coverage come from `deliveryAreaConfigAtom`
 * (`GET /public/merchants/{id}/delivery-area`), not the Open Delivery catalog.
 *
 * Cost model: at most one reverse (GPS) plus one forward geocode when the map
 * opens. Confirming the pin and dragging it do not geocode.
 * The fee itself is never trusted from here: merchant-service-core re-checks the
 * pin against the typed address (docs/DELIVERY-QUOTE.md, pin check).
 */

type AddressStep = "entry" | "details" | "map" | "finish"

/** Accent-insensitive, case-insensitive comparison key for city/state/district names. */
function normalizeText(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()
}

function buildFullAddress(parts: {
  street?: string
  number?: string
  district?: string
  city?: string
  state?: string
  postalCode?: string
}): string {
  return [parts.street, parts.number, parts.district, parts.city, parts.state, parts.postalCode]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join(", ")
}

async function geocodeAddressOnce(
  address: string,
): Promise<{ lat: number; lng: number; precision: AnchorPrecision } | null> {
  if (!address.trim()) return null
  try {
    const data = await forwardGeocode(address)
    if (data.status !== "OK" || data.results.length === 0) return null
    const first = data.results[0]
    return { lat: first.geometry.location.lat, lng: first.geometry.location.lng, precision: geocodePrecision(first) }
  } catch {
    return null
  }
}

export default function NewAddress({ redirectTo, text }: { redirectTo: string; text: string }) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<AddressStep>("entry")
  const [anchor, setAnchor] = useState<PinAnchor | null>(null)

  const basicInfo = useAtomValue(basicInfoAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)

  usePageBackground()

  const storeCity = basicInfo?.address?.city || basicInfo?.address?.district || ""
  const storeState = basicInfo?.address?.state || ""

  const isNeighborhoodMode = deliveryAreaConfig?.mode === "NEIGHBORHOOD"
  const servedCities = useMemo(
    () => (deliveryAreaConfig?.mode === "NEIGHBORHOOD" ? deliveryAreaConfig.cities : []),
    [deliveryAreaConfig],
  )
  const flatNeighborhoods = useMemo(
    () => (deliveryAreaConfig?.mode === "NEIGHBORHOOD" ? deliveryAreaConfig.neighborhoods : []),
    [deliveryAreaConfig],
  )
  const hasCities = servedCities.length > 0

  const {
    handleSubmit,
    formState: { errors },
    watch,
    register,
    setValue,
    getValues,
    reset,
    trigger,
  } = useForm<AddressSchemaType>({
    resolver: zodResolver(addressSchema, {
      error: (issue) => {
        console.error(issue)
        return { message: "Erro de validação" }
      },
    }),
  })

  const {
    position,
    setPosition,
    cityMismatch,
    setCityMismatch,
    lastCepRef,
    fallbackPositionRef,
    fallbackSourceRef,
    handleLocation,
    fetchCoordsByCep,
  } = useAddressEntryGeocoding({
    isNeighborhoodMode,
    storeCity,
    storeState,
    setLoading,
    setValue,
    setStep,
  })

  const fields = watch()

  const cityOptions: LocationPickerOption[] = useMemo(
    () => servedCities.map((city) => ({ id: city.id, name: city.name })),
    [servedCities],
  )

  const selectedCity = useMemo(
    () =>
      servedCities.find(
        (city) =>
          normalizeText(city.name) === normalizeText(fields.city || "") &&
          normalizeText(city.state) === normalizeText(fields.state || ""),
      ),
    [servedCities, fields.city, fields.state],
  )

  const neighborhoodOptions: LocationPickerOption[] = useMemo(() => {
    const source = hasCities ? selectedCity?.neighborhoods ?? [] : flatNeighborhoods
    return source.map((neighborhood) => ({ id: neighborhood.id, name: neighborhood.name }))
  }, [hasCities, selectedCity, flatNeighborhoods])

  useEffect(() => {
    if (!isNeighborhoodMode || step !== "entry") return
    if (!hasCities) {
      if (storeCity) setValue("city", storeCity)
      if (storeState) setValue("state", storeState)
    }
    setValue("postalCode", "")
    setStep("details")
  }, [isNeighborhoodMode, hasCities, step, storeCity, storeState, setValue])

  function selectCity(option: LocationPickerOption) {
    const city = servedCities.find((c) => c.id === option.id)
    if (!city) return
    setValue("city", city.name, { shouldValidate: true })
    setValue("state", city.state, { shouldValidate: true })

    const currentDistrict = getValues("district")
    const districtStillServed = city.neighborhoods.some(
      (neighborhood) => normalizeText(neighborhood.name) === normalizeText(currentDistrict || ""),
    )
    if (!districtStillServed) setValue("district", "")
  }

  function selectNeighborhood(option: LocationPickerOption) {
    setValue("district", option.name, { shouldValidate: true })
  }

  useEffect(() => {
    if (step !== "entry") return
    const cep = fields.postalCode
    if (cep && cep.length === 9 && cep !== lastCepRef.current) {
      lastCepRef.current = cep
      void fetchCoordsByCep(cep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.postalCode, step])

  function handleReturn() {
    if (step === "finish") {
      setStep("map")
      return
    }
    if (step === "map") {
      setStep("details")
      return
    }
    if (step === "details") {
      if (isNeighborhoodMode) {
        navigate(redirectTo)
        return
      }
      setStep("entry")
      setCityMismatch({ mismatch: false, city: "", state: "" })
      setPosition(undefined)
      setAnchor(null)
      fallbackPositionRef.current = null
      fallbackSourceRef.current = null
      reset()
      lastCepRef.current = ""
      return
    }
    navigate(redirectTo)
  }

  const stepTitle =
    step === "entry"
      ? "Endereço de entrega"
      : step === "details"
        ? "Preencha seu endereço"
        : step === "map"
          ? "Confirme no mapa"
          : "Complete o endereço"

  /** Validate details, then open the map on the typed address geocode and its drift circle. */
  const goToMapOrSaveNeighborhood = async () => {
    const ok = await trigger(["street", "number", "district", "city", "state"])
    if (!ok) {
      showToast.error("Confira os campos obrigatórios antes de continuar.")
      return
    }
    if (cityMismatch.mismatch) {
      showToast.error("Esse endereço fica fora da área da loja.")
      return
    }

    if (isNeighborhoodMode) {
      await handleSubmit(saveAddress)()
      return
    }

    setLoading(true)
    try {
      const values = getValues()
      const full = buildFullAddress(values)
      const previous =
        position ??
        fallbackPositionRef.current ??
        (typeof values.latitude === "number" &&
        typeof values.longitude === "number" &&
        values.latitude !== 0 &&
        values.longitude !== 0
          ? { lat: values.latitude, lng: values.longitude }
          : null)

      const refined = full.trim() ? await geocodeAddressOnce(full) : null

      if (refined) {
        setPosition({ lat: refined.lat, lng: refined.lng })
        setAnchor({ lat: refined.lat, lng: refined.lng, limitMeters: PIN_DRIFT_LIMIT_METERS[refined.precision] })
        setValue("latitude", refined.lat)
        setValue("longitude", refined.lng)
      } else if (previous) {
        setPosition(previous)
        setAnchor({
          ...previous,
          limitMeters: fallbackSourceRef.current === "gps" ? PIN_DRIFT_LIMIT_METERS.street : null,
        })
        setValue("latitude", previous.lat)
        setValue("longitude", previous.lng)
      } else if (basicInfo?.address?.latitude && basicInfo?.address?.longitude) {
        setAnchor(null)
        setPosition({
          lat: basicInfo.address.latitude,
          lng: basicInfo.address.longitude,
        })
        showToast.error(
          "Não achamos o ponto exato. Ajuste o pin no mapa no local da entrega.",
        )
      } else {
        showToast.error(
          "Não achamos o ponto no mapa. Tente outro número/CEP ou posicione o pin manualmente.",
        )
      }

      setStep("map")
    } finally {
      setLoading(false)
    }
  }

  const saveAddress: SubmitHandler<AddressSchemaType> = async (data) => {
    const lat = position?.lat ?? data.latitude ?? 0
    const lng = position?.lng ?? data.longitude ?? 0

    if (!isNeighborhoodMode && (!lat || !lng)) {
      showToast.error("Confirme a posição no mapa antes de salvar.")
      setStep("map")
      return
    }

    const finalData: Omit<Address, "id"> = {
      ...data,
      postalCode: (data.postalCode || "").split("-").join(""),
      country: "BR",
      complement: data.complement,
      latitude: lat,
      longitude: lng,
    }

    if (deliveryService || deliveryAreaConfig) {
      const status = getAddressServiceAreaStatus(
        finalData as Address,
        deliveryService?.serviceArea,
        basicInfo?.address,
        deliveryAreaConfig,
      )
      if (status.availablePolygons.length === 0) {
        showToast.error("Esse ponto fica fora da área de entrega da loja. Ajuste o pin ou o endereço.")
        if (!isNeighborhoodMode) setStep("map")
        return
      }
    }

    setLoading(true)
    try {
      await createAddress(finalData)
      navigate(redirectTo)
    } catch (error) {
      console.error(error)
      showToast.error("Não deu pra salvar o endereço. Tente de novo.")
    } finally {
      setLoading(false)
    }
  }

  /** Map step: pin moves only update local state — zero API calls. */
  const handlePinMove = useCallback((lat: number, lng: number) => {
    setPosition({ lat, lng })
    setValue("latitude", lat)
    setValue("longitude", lng)
  }, [setValue, setPosition])

  const outOfReach = step === "map" && pinOutOfReach(anchor, position)

  /** Confirms the pin coordinates and keeps the street, number and district the customer typed. */
  const confirmMap = () => {
    if (!position?.lat || !position?.lng) {
      showToast.error("Toque no mapa ou arraste o pin até o local da entrega.")
      return
    }
    if (outOfReach) {
      showToast.error("Aproxime o pin do endereço informado ou corrija o endereço.")
      return
    }
    setValue("latitude", position.lat)
    setValue("longitude", position.lng)
    setStep("finish")
  }

  return (
    <>
      {loading && <LoadingOverlay title="Carregando informações" />}

      <PageContainer
        top="header-section"
        className={`flex flex-col gap-8 ${step === "map" ? "pb-36" : "pb-28"}`}
      >
        {step === "entry" ? (
          <Heading level={1} className="sr-only">
            {stepTitle}
          </Heading>
        ) : (
          <div className="flex items-center gap-4">
            <IconButton
              icon={Back}
              label="Voltar à etapa anterior"
              variant="outline"
              size="icon-sm"
              onClick={handleReturn}
            />
            <div className="flex flex-col">
              <Heading level={1} size="title">
                {stepTitle}
              </Heading>
              {!isNeighborhoodMode && (
                <Text size="xs" tone="muted">
                  {step === "details"
                    ? "Etapa 1 de 3 · dados do endereço"
                    : step === "map"
                      ? "Etapa 2 de 3 · posição no mapa"
                      : "Etapa 3 de 3 · complemento"}
                </Text>
              )}
            </div>
          </div>
        )}
        {step === "entry" && (
          <AddressEntryStep
            text={text}
            loading={loading}
            errors={errors}
            register={register}
            cityMismatch={cityMismatch}
            onUseLocation={handleLocation}
          />
        )}

        {step === "details" && (
          <AddressDetailsForm
            register={register}
            errors={errors}
            fields={fields}
            loading={loading}
            cityMismatchBlocks={cityMismatch.mismatch}
            isNeighborhoodMode={isNeighborhoodMode}
            hasCities={hasCities}
            cityOptions={cityOptions}
            neighborhoodOptions={neighborhoodOptions}
            onSelectCity={selectCity}
            onSelectNeighborhood={selectNeighborhood}
            onSubmit={() => void goToMapOrSaveNeighborhood()}
          />
        )}

        {step === "map" && !isNeighborhoodMode && (
          <AddressMapStep
            fields={fields}
            position={position}
            anchor={anchor}
            outOfReach={outOfReach}
            loading={loading}
            onPinMove={handlePinMove}
            onConfirm={() => void confirmMap()}
            onBackToDetails={() => setStep("details")}
          />
        )}

        {step === "finish" && !isNeighborhoodMode && (
          <AddressFinishStep
            fields={fields}
            register={register}
            errors={errors}
            loading={loading}
            onSave={() => void handleSubmit(saveAddress)()}
            onEditAddress={() => setStep("details")}
          />
        )}
      </PageContainer>
    </>
  )
}
